import styled from "@emotion/styled";
import { useNavigate } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { RELEVANCY_SEARCH_NEW_USERS, TEAM_USAGE_STATS, useVariableValue } from "lib/featureFlags";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { companyHasSubscription, useSubscription } from "lib/hooks/subscription";
import { useCognismByot } from "lib/hooks/useCognismByot";
import { useUnifiedContacts } from "lib/hooks/useUnifiedContacts";
import { settingsRoutes } from "lib/routes";
import { toAndSearch } from "lib/spaNavigation";
import { SubscriptionEnum } from "lib/types/graphQLEnums";
import NavItem from "./NavItem";
import { EXPANDEDWIDTH, MainNav } from "./VerticalNav.styles";

function SettingsNav() {
  const { hasDataTypes } = useSubscription();
  const { data: currentUser } = useCurrentUser();
  const navigate = useNavigate();

  function handleBack() {
    // Stored value may include a query string, so split path/search via toAndSearch.
    void navigate(toAndSearch(sessionStorage.getItem("lastMainNavPageVisited") || "/"));
  }

  const isRelevancySearchEnabled = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const isTeamUsageStatsEnabled = useVariableValue(TEAM_USAGE_STATS, false);
  const hasBidsAccess = hasDataTypes("BIDS");

  const isUnifiedContactsEnabled = useUnifiedContacts();
  const isCognismByotEnabled = useCognismByot();
  const hasContactsAccess = hasDataTypes("CONTACTS");
  const isCompanyAdmin = currentUser?.isCompanyAdmin || currentUser?.isAdmin;

  // Enterprise SSO is an Expert-plan feature licensed to the company, not to the individual seat.
  // The route itself redirects anyone outside an Expert company, so hiding the nav item here just
  // keeps it out of sight.
  const isCompanyOnExpertPlan = companyHasSubscription(currentUser, SubscriptionEnum.Expert);

  return (
    <Wrapper>
      <TopRow>
        <Button icon="chevronLeft" buttonStyle="inverse" onClick={handleBack} />
        <Text h2>Settings</Text>
      </TopRow>
      <MainNav>
        {!isRelevancySearchEnabled && (
          <NavItem title="Signal settings" icon="signal01" path={settingsRoutes.signalSettings} />
        )}
        <NavItem title="Account" icon="user01" path={settingsRoutes.account} />
        <NavItem title="Subscription" icon="creditCard02" path={settingsRoutes.subscription} />
        <NavItem title="Security" icon="lock01" path={settingsRoutes.security} />
        {isCompanyOnExpertPlan && (
          <NavItem title="SSO" icon="building07" path={settingsRoutes.sso} />
        )}
        <NavItem title="Lead stages" icon="target04" path={settingsRoutes.leadStages} />
        <NavItem title="Bid stages" icon="feather" path={settingsRoutes.bidStages} />
        {hasBidsAccess && (
          <NavItem title="Tone of voice" icon="edit02" path={settingsRoutes.toneOfVoice} />
        )}
        {hasBidsAccess && (
          <NavItem title="Quick prompts" icon="magicWand" path={settingsRoutes.quickPrompts} />
        )}
        <NavItem title="Requirement summary" icon="settings04" path={settingsRoutes.bidSelfServe} />
        <NavItem
          title="Qualification report"
          icon="clipboardCheck"
          path={settingsRoutes.qualificationReport}
        />
        <NavItem title="Notifications" icon="bell03" path={settingsRoutes.notifications} />
        <NavItem title="Team management" icon="users01" path={settingsRoutes.team} />
        {isTeamUsageStatsEnabled && (
          <NavItem title="Usage" icon="barChart12" path={settingsRoutes.usage} />
        )}
        <NavItem title="Integrations" icon="integration" path={settingsRoutes.integrations} />
        {isUnifiedContactsEnabled && hasContactsAccess && (
          <NavItem title="Contacts" icon="users01" path={settingsRoutes.contacts} />
        )}
        {isCognismByotEnabled && hasContactsAccess && isCompanyAdmin && (
          <NavItem title="Cognism token" icon="lock01" path={settingsRoutes.cognismToken} />
        )}
      </MainNav>
    </Wrapper>
  );
}

export default SettingsNav;

const Wrapper = styled.div(({ theme }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  height: "100%",
  backgroundColor: theme.colors.white,
  zIndex: 4,
  width: EXPANDEDWIDTH,
  padding: 24,
  borderRight: `1px solid ${theme.colors.sysBorderDecorative}`,
}));

const TopRow = styled.div({
  display: "flex",
  alignItems: "center",
  gap: 4,
});
